import { createFileRoute, useParams } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useState } from 'react';
import { ConfirmActionDialog } from '@/components/Dialog/ConfirmActionDialog';
import { VStack } from '@/components/Layout';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { DriftConfirmDialog } from './-components/DriftConfirmDialog';
import { DriftWarningCard } from './-components/DriftWarningCard';
import EditPlanDialog from './-components/EditPlanDialog';
import { PlanHeader } from './-components/Header';
import PlanHeaderSkeleton from './-components/HeaderSkeleton';
import RestructuringChart from './-components/RestructuringChart';
import RestructuringChartSkeleton from './-components/RestructuringChartSkeleton';
import { usePlanCompletion } from './-hooks/usePlanCompletion';
import { useRestructuringChartData } from './-useRestructuringChartData';
import { PlanDetailSearchSchema } from './-zod-schema';
export const Route = createFileRoute('/_staff/organization/plan/$id/')({
  component: RouteComponent,
  validateSearch: zodValidator(PlanDetailSearchSchema),
  loaderDeps: ({
    search
  }) => ({
    search
  })
});
function RouteComponent() {
  const {
    id
  } = useParams({
    from: '/_staff/organization/plan/$id/'
  });
  const {
    detail,
    organizations,
    isLoading,
    initialCollapsed,
    onCollapsedChange
  } = useRestructuringChartData(id);
  const completion = usePlanCompletion(id, detail);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.organization.plan.detailPage'
  });
  const tc = useCommonTranslation();
  const handleEdit = () => {
    setIsEditOpen(true);
  };
  if (isLoading || !detail) {
    return <VStack className="gap-4">
        <PlanHeaderSkeleton />
        <div className="px-12 py-5">
          <RestructuringChartSkeleton />
        </div>
      </VStack>;
  }
  return <VStack className="gap-4">
      <PlanHeader data={detail} onEdit={handleEdit} onApprove={completion.openConfirm}
    // ドリフト取得前に確定すると署名を付けられず必ず 409 になるため、判明まで押させない
    isApproving={completion.isCompleting || completion.isDriftLoading} />

      {completion.showDriftWarning && <div className="px-12">
          <DriftWarningCard summary={completion.driftSummary} />
        </div>}

      <RestructuringChart organizations={organizations} status={detail.status} initialCollapsed={initialCollapsed} onCollapsedChange={onCollapsedChange} />

      <EditPlanDialog isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} data={detail} />
      {completion.hasDrift ? <DriftConfirmDialog isOpen={completion.isConfirmOpen} onClose={completion.closeConfirm} onConfirm={completion.confirm} planName={detail.name} summary={completion.driftSummary} isLoading={completion.isCompleting} /> : <ConfirmActionDialog isOpen={completion.isConfirmOpen} onClose={completion.closeConfirm} onConfirm={completion.confirm} title={{
      resource: detail.name,
      action: tc('action.approve')
    }} description={t('completeDialog.description')} okLabel={tc('action.approve')} isLoading={completion.isCompleting} />}
    </VStack>;
}